import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { FC, ReactNode } from 'react';
import ActionSheet from 'src/common/components/ActionSheet';
import Dropdown from 'src/common/components/Dropdown';
import DropdownDS22, { DropdownOption, Variant, DropdownPosition } from 'src/common/components/DropdownDS22';
import Icon from 'src/common/components/IconDS22';
import getClassNames from 'src/common/utils/getClassNames';
import styles from './ActionsDropdown.module.scss';

export type ActionsDropdownProps = {
  options: DropdownOption[];
  handleSelect: (item: DropdownOption) => void;
  label?: ReactNode;
  showCaret?: boolean;
  variant?: Variant;
  dropdownPosition?: DropdownPosition;
  mode?: 'menu' | 'select';
  defaultOption?: DropdownOption;
  classNameModifier?: string;
  disabled?: HTMLButtonElement['disabled'];
  fullWidth?: boolean;
  error?: boolean;
  selectedItemRenderer?: (option: DropdownOption) => React.ReactNode;
};
const ActionsDropdown: FC<ActionsDropdownProps> = ({
  options,
  handleSelect,
  label = '',
  showCaret = true,
  variant = 'white',
  dropdownPosition = 'left',
  mode = 'menu',
  defaultOption,
  classNameModifier,
  disabled,
  fullWidth,
  error,
  selectedItemRenderer,
}) => {
  const [t] = useTranslation('admin-listings');
  const selectedOption = options.find(({ id }) => id === defaultOption?.id);
  const text = selectedOption ? selectedOption.dropdownLabel : label || t('landing-page--actions');
  return (
    <div className={getClassNames(styles, 'root', classNames(classNameModifier))}>
      <div className={getClassNames(styles, 'desktop', variant)}>
        <DropdownDS22
          instructionLabel={text}
          variant={variant}
          dropdownPosition={dropdownPosition}
          mode={mode}
          options={options}
          onChange={handleSelect}
          defaultOption={defaultOption}
          disabled={disabled}
          fullWidth={fullWidth}
          error={error}
          selectedItemRenderer={selectedItemRenderer}
          displayCaret={showCaret}
        />
      </div>
      <Dropdown
        disableOnClickOutside
        className={styles.mobile}
      >
        {({ close, isOpen, toggle }) => (
          <>
            <button
              className={getClassNames(styles, 'button', classNames(variant, { fullWidth, error }))}
              id="actionMenu-button"
              type="button"
              onClick={toggle}
              aria-haspopup="true"
              aria-controls="menu"
              disabled={disabled}
              aria-disabled={disabled}
            >
              {text}
              {showCaret && (
                <span className={getClassNames(styles, 'buttonCaret', classNames({ error }))}>
                  <Icon
                    id="caret-down"
                    size="s"
                  />
                </span>
              )}
            </button>
            <ActionSheet isOpen={isOpen} close={close}>
              <ul
                id="actionMenu"
                className={styles.list}
                role="menu"
                aria-labelledby="actionMenu-button"
              >
                {options.map((option: DropdownOption) => (
                  <li
                    role="presentation"
                    className={getClassNames(styles, 'listItem', classNames({
                      disabled: option.disabled,
                      selected: option.id === selectedOption?.id,
                    }))}
                    key={option.id}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        handleSelect(option);
                        close();
                      }}
                    >
                      {option.icon && <Icon id={option.icon} size="m" />}
                      {option.dropdownLabel}
                    </button>
                  </li>
                ))}
              </ul>
            </ActionSheet>
          </>
        )}
      </Dropdown>
    </div>
  );
};

export default ActionsDropdown;
